import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { cloneChatFromMessage, inspectSnapshotItinerary } from 'tripPlanner/api/v3'
import type { TripItineraryData } from 'tripPlanner/api/types'

/**
 * Staff-only "rewind" (chat time machine).
 *
 * When a staff member views ANOTHER user's trip chat they can, per assistant turn:
 *  - inspect() — swap the itinerary panel to a read-only snapshot of how the itinerary
 *    stood at that turn (no mutation; the live data is restored on exit / chat change);
 *  - fork() — copy the trip at that turn into a NEW chat on their own account and navigate
 *    to it.
 *
 * The context is always present with a safe default (canStaffRewind: false), so surfaces
 * that render the chat without the provider — e.g. the offer-page assistant — keep working.
 */
export interface StaffRewindContextValue {
  /** Whether the rewind affordances should be shown at all (staff viewing another's trip). */
  canStaffRewind: boolean
  /** The assistant message currently being inspected, if any. */
  inspectedMessageId: string | undefined
  /** Snapshot itinerary for the inspected turn; overrides the live panel while set. */
  snapshotItinerary: TripItineraryData | undefined
  isInspecting: boolean
  isInspectLoading: boolean
  inspect: (messageId: string) => void
  exitInspect: () => void
  /** Fork the trip at a turn into a new staff-owned chat; navigates on success. */
  fork: (messageId: string) => void
  forkingMessageId: string | undefined
  // ── Stepper (prev/next arrow navigation through trip states) ──
  /** Total number of steppable states — one per finalized assistant turn. */
  stateCount: number
  /** 1-based position of the state currently shown; equals stateCount when live/newest. */
  currentStatePosition: number
  /** Step one assistant turn back in time (toward older states). */
  stepBack: () => void
  /** Step one assistant turn forward in time (toward the live/newest state). */
  stepForward: () => void
  canStepBack: boolean
  canStepForward: boolean
}

const DEFAULT_VALUE: StaffRewindContextValue = {
  canStaffRewind: false,
  inspectedMessageId: undefined,
  snapshotItinerary: undefined,
  isInspecting: false,
  isInspectLoading: false,
  inspect: () => {},
  exitInspect: () => {},
  fork: () => {},
  forkingMessageId: undefined,
  stateCount: 0,
  currentStatePosition: 0,
  stepBack: () => {},
  stepForward: () => {},
  canStepBack: false,
  canStepForward: false,
}

export const StaffRewindContext = createContext<StaffRewindContextValue>(DEFAULT_VALUE)

export function useStaffRewind(): StaffRewindContextValue {
  return useContext(StaffRewindContext)
}

interface UseStaffRewindValueArgs {
  chatId: number | undefined
  region: string
  /** True only when staff is viewing a trip that is not their own. */
  canStaffRewind: boolean
  /**
   * Ids of finalized assistant turns, oldest → newest — one steppable "state" each.
   * The stepper walks this list; the newest turn is the live/current state.
   */
  assistantMessageIds: Array<string>
  /** Navigate to a chat by id (provided by the page layer, which owns routing). */
  navigateToChat: (chatId: number) => void
}

/**
 * Builds the StaffRewind context value. Lives as a hook (rather than a wrapping provider)
 * so TripPlannerChatProvider can both provide it to descendants AND read `snapshotItinerary`
 * to override the itinerary panel — a wrapping provider couldn't feed its own parent.
 *
 * Navigation is injected (`navigateToChat`) rather than dispatched here, so this hook adds
 * no redux/router coupling to the provider it runs in.
 */
export function useStaffRewindValue({ chatId, region, canStaffRewind, assistantMessageIds, navigateToChat }: UseStaffRewindValueArgs): StaffRewindContextValue {
  const [inspectedMessageId, setInspectedMessageId] = useState<string | undefined>(undefined)
  const [forkingMessageId, setForkingMessageId] = useState<string | undefined>(undefined)

  // Reset any inspection when the open chat changes, or when rewind access is lost — a
  // snapshot is chat-scoped and must never linger onto a chat the viewer owns.
  useEffect(() => {
    setInspectedMessageId(undefined)
  }, [chatId, canStaffRewind])

  const enabled = canStaffRewind && chatId !== undefined && inspectedMessageId !== undefined
  const snapshotQuery = useQuery({
    queryKey: ['staffRewindSnapshot', chatId ?? 0, inspectedMessageId ?? '', region],
    queryFn: () => inspectSnapshotItinerary({ chatId: chatId as number, messageId: inspectedMessageId as string, region }),
    enabled,
    staleTime: Infinity,
  })

  // Surface a fetch failure and drop back to the live view rather than stranding the banner.
  useEffect(() => {
    if (snapshotQuery.isError) {
      showSnackbar('Could not load the itinerary for that message.', 'critical')
      setInspectedMessageId(undefined)
    }
  }, [snapshotQuery.isError])

  const inspect = useCallback((messageId: string) => {
    setInspectedMessageId((current) => (current === messageId ? undefined : messageId))
  }, [])

  const exitInspect = useCallback(() => {
    setInspectedMessageId(undefined)
  }, [])

  // Stepper. The list is oldest → newest; the newest turn is the live state, represented by
  // `inspectedMessageId === undefined` (so the default view needs no snapshot fetch). Stepping
  // back walks toward older turns; stepping forward off the second-newest returns to live.
  const stateCount = assistantMessageIds.length
  // Index of the state on screen; the live/newest state sits at the last index.
  const currentIndex = inspectedMessageId === undefined
    ? stateCount - 1
    : assistantMessageIds.indexOf(inspectedMessageId)
  const canStepBack = currentIndex > 0
  const canStepForward = inspectedMessageId !== undefined && currentIndex !== -1

  const stepBack = useCallback(() => {
    setInspectedMessageId((current) => {
      const index = current === undefined ? assistantMessageIds.length - 1 : assistantMessageIds.indexOf(current)
      if (index <= 0) {
        return current
      }
      return assistantMessageIds[index - 1]
    })
  }, [assistantMessageIds])

  const stepForward = useCallback(() => {
    setInspectedMessageId((current) => {
      if (current === undefined) {
        return current
      }
      const index = assistantMessageIds.indexOf(current)
      // Stepping onto (or past) the newest turn means returning to the live state.
      if (index === -1 || index + 1 >= assistantMessageIds.length - 1) {
        return undefined
      }
      return assistantMessageIds[index + 1]
    })
  }, [assistantMessageIds])

  const fork = useCallback((messageId: string) => {
    if (chatId === undefined || forkingMessageId !== undefined) {
      return
    }
    setForkingMessageId(messageId)
    cloneChatFromMessage({ chatId, toMessageId: messageId, region })
      .then((result) => {
        showSnackbar('Copied this trip to your account.', 'success')
        navigateToChat(result.chat.id)
      })
      .catch(() => {
        showSnackbar('Could not copy this trip.', 'critical')
      })
      .finally(() => {
        setForkingMessageId(undefined)
      })
  }, [chatId, region, forkingMessageId, navigateToChat])

  return useMemo<StaffRewindContextValue>(() => ({
    canStaffRewind,
    inspectedMessageId,
    snapshotItinerary: enabled ? snapshotQuery.data : undefined,
    isInspecting: inspectedMessageId !== undefined,
    isInspectLoading: enabled && snapshotQuery.isFetching,
    inspect,
    exitInspect,
    fork,
    forkingMessageId,
    stateCount,
    currentStatePosition: currentIndex + 1,
    stepBack,
    stepForward,
    canStepBack,
    canStepForward,
  }), [
    canStaffRewind, inspectedMessageId, enabled, snapshotQuery.data, snapshotQuery.isFetching,
    inspect, exitInspect, fork, forkingMessageId,
    stateCount, currentIndex, stepBack, stepForward, canStepBack, canStepForward,
  ])
}
